import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Plus, X, Send, Upload, Trash2, RotateCcw, Truck } from "lucide-react";
import api, { apiErrorDetail } from "../lib/api";
import AdminReturns from "./AdminReturns";

const STATUSES = ["pending_payment", "paid", "submitted_to_qikink", "processing", "shipped", "delivered", "returned", "cancelled"];
const STATUS_COLOR = { pending_payment: "text-[#3B82F6]", paid: "text-acid", submitted_to_qikink: "text-[#10B981]", processing: "text-[#3B82F6]", shipped: "text-[#22C55E]", delivered: "text-acid", returned: "text-[#F59E0B]", cancelled: "text-volt" };

const inputCls = "w-full bg-[#151821] border border-white/15 px-3 py-2.5 font-mono text-[11px] text-ink placeholder:text-mute focus:outline-none focus:border-acid";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/login", { email, password });
      onLogin();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
    setBusy(false);
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#0E1015] p-8">
        <p className="font-mono text-[10px] tracking-[0.3em] text-volt mb-2">RESTRICTED ACCESS</p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-2xl mb-8">RVIOR COMMAND</h1>
        <input data-testid="admin-email-input" className={inputCls} placeholder="ADMIN EMAIL" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="admin-password-input" className={`${inputCls} mt-3`} placeholder="PASSWORD" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="admin-login-btn" disabled={busy} type="submit" className="w-full mt-6 bg-volt text-void font-display font-bold text-sm tracking-widest py-3.5 disabled:opacity-40 hover:bg-acid transition-colors">
          {busy ? "AUTHENTICATING…" : "ENTER"}
        </button>
      </form>
    </main>
  );
}

function ProductForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({
    title: initial?.title || "", slug: initial?.slug || "", code: initial?.code || "",
    price: initial?.price ?? "", original_price: initial?.original_price ?? "", tag: initial?.tag || "",
    gsm: initial?.gsm || "", fit: initial?.fit || "", image: initial?.image || "", images: initial?.images || [],
    description: initial?.description || "",
    colors: (initial?.colors || [{ name: "", hex: "#0B0B0D", sku: "" }]).map((c) => `${c.name}|${c.hex}|${c.sku}`).join("\n"),
    sizes: (initial?.sizes || []).join(", "), active: initial?.active ?? true, sort_order: initial?.sort_order ?? 99,
    variant_skus: Object.entries(initial?.variant_skus || {}).map(([k, v]) => `${k}|${v}`).join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extraUploading, setExtraUploading] = useState(false);
  const [colorImages, setColorImages] = useState(
    Object.fromEntries((initial?.colors || []).filter((c) => c.image).map((c) => [c.name, c.image]))
  );
  const [colorTarget, setColorTarget] = useState(null);
  const [colorPhotoBusy, setColorPhotoBusy] = useState(false);
  const fileRef = useRef(null);
  const extraRef = useRef(null);
  const colorFileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const uploadColourPhoto = async (file) => {
    const target = colorTarget;
    if (!file || !target) return;
    if (!/\.(jpe?g|png|webp|gif)$/i.test(file.name)) return toast.error("Use a JPG, PNG, WEBP or GIF image");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large — max 8 MB");
    setColorPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setColorImages((prev) => ({ ...prev, [target]: data.url }));
      toast.success(`${target} photo uploaded`);
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
    setColorPhotoBusy(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp|gif)$/i.test(file.name)) return toast.error("Use a JPG, PNG, WEBP or GIF image");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large — max 8 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
    setUploading(false);
  };

  const uploadExtra = async (files) => {
    const room = 6 - f.images.length;
    const list = [...(files || [])].slice(0, room);
    if (list.length === 0) return toast.error("Maximum 6 extra photos");
    setExtraUploading(true);
    for (const file of list) {
      if (!/\.(jpe?g|png|webp|gif)$/i.test(file.name)) { toast.error(`${file.name}: use JPG, PNG, WEBP or GIF`); continue; }
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: too large — max 8 MB`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((prev) => ({ ...prev, images: [...prev.images, data.url] }));
      } catch (err) {
        toast.error(apiErrorDetail(err));
      }
    }
    setExtraUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const colors = f.colors.split("\n").filter(Boolean).map((l) => {
      const [name, hex, sku] = l.split("|").map((s) => s.trim());
      return { name: name || "", hex: hex || "#0B0B0D", sku: sku || "", image: (name && colorImages[name]) || "" };
    });
    const body = {
      title: f.title, slug: f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      code: f.code, price: Number(f.price), original_price: f.original_price ? Number(f.original_price) : null,
      tag: f.tag, gsm: f.gsm, fit: f.fit, image: f.image, images: f.images || [], description: f.description,
      colors, sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      variant_skus: Object.fromEntries(f.variant_skus.split("\n").filter(Boolean).map((l) => {
        const parts = l.split("|").map((s) => s.trim()).filter(Boolean);
        let colour, size, sku;
        if (parts.length >= 3) { [colour, size, sku] = parts; }
        else if (parts.length === 2) {
          const head = parts[0];
          const segs = head.includes("->") ? head.split("->") : head.split(/\s+[-–]\s+/);
          if (segs.length >= 2) { size = segs[segs.length - 1]; colour = segs.slice(0, -1).join(" "); }
          else { const toks = head.split(/\s+/); size = toks.pop() || ""; colour = toks.join(" "); }
          sku = parts[1];
        } else return null;
        colour = (colour || "").trim(); size = (size || "").trim(); sku = (sku || "").trim();
        return colour && size && sku ? [`${colour}|${size}`, sku] : null;
      }).filter(Boolean)),
      active: f.active, sort_order: Number(f.sort_order),
    };
    try {
      if (initial?._id) await api.patch(`/admin/products/${initial._id}`, body);
      else await api.post("/admin/products", body);
      toast.success(initial?._id ? "Product updated" : "Product added");
      onSaved();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#0E1015] p-5 space-y-3 mb-6" data-testid="admin-product-form">
      <div className="flex justify-between items-center">
        <p className="font-mono text-[10px] tracking-[0.3em] text-dim">{initial?._id ? `EDIT — ${initial.title}` : "NEW PRODUCT"}</p>
        <button type="button" onClick={onCancel} className="text-mute hover:text-white"><X size={14} /></button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <input required className={inputCls} placeholder="TITLE *" value={f.title} onChange={set("title")} />
        <input className={inputCls} placeholder="CODE (rv-007)" value={f.code} onChange={set("code")} />
        <input required className={inputCls} placeholder="PRICE *" type="number" value={f.price} onChange={set("price")} />
        <input className={inputCls} placeholder="MRP" type="number" value={f.original_price} onChange={set("original_price")} />
        <input className={inputCls} placeholder="TAG" value={f.tag} onChange={set("tag")} />
        <input className={inputCls} placeholder="GSM / FABRIC" value={f.gsm} onChange={set("gsm")} />

        {/* PHOTO UPLOAD */}
        <div className="md:col-span-3 border border-white/10 bg-[#060709] p-4 flex flex-wrap items-center gap-4">
          {f.image ? (
            <img src={f.image} alt="Product" className="w-16 h-20 object-cover border border-white/15" />
          ) : (
            <div className="w-16 h-20 border border-dashed border-white/20 flex items-center justify-center text-mute text-[9px] font-mono text-center">NO<br />PHOTO</div>
          )}
          <div className="flex-1 min-w-[200px]">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid="product-photo-input"
              onChange={(e) => uploadPhoto(e.target.files?.[0])}
            />
            <button
              type="button"
              data-testid="product-photo-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 bg-volt text-void font-display font-bold text-xs tracking-widest px-6 py-3 hover:bg-acid transition-colors disabled:opacity-40"
            >
              <Upload size={13} /> {uploading ? "UPLOADING…" : "UPLOAD PHOTO"}
            </button>
            <p className="mt-2 font-mono text-[9px] tracking-widest text-mute">
              FROM YOUR PHONE OR COMPUTER · JPG / PNG / WEBP · UP TO 8 MB — THIS PHOTO SHOWS ON THE STORE
            </p>
            {f.image && (
              <button type="button" onClick={() => setF({ ...f, image: "" })} className="mt-2 font-mono text-[9px] tracking-widest text-volt hover:text-white">
                REMOVE PHOTO
              </button>
            )}
          </div>
          <input className={`${inputCls} md:w-64`} placeholder="…or paste an image URL" value={f.image.startsWith("/api/") ? "" : f.image} onChange={set("image")} />
        </div>

        {/* EXTRA ANGLE PHOTOS */}
        <div className="md:col-span-3 border border-white/10 bg-[#060709] p-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={extraRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid="product-extra-photos-input"
              onChange={(e) => { uploadExtra(e.target.files); e.target.value = ""; }}
            />
            <button
              type="button"
              data-testid="product-extra-photos-btn"
              onClick={() => extraRef.current?.click()}
              disabled={extraUploading}
              className="flex items-center gap-2 border border-acid text-acid font-display font-bold text-xs tracking-widest px-6 py-3 hover:bg-acid hover:text-void transition-colors duration-300 disabled:opacity-40"
            >
              <Upload size={13} /> {extraUploading ? "UPLOADING…" : "ADD MORE PHOTOS — OTHER ANGLES"}
            </button>
            <p className="font-mono text-[9px] tracking-widest text-mute flex-1 min-w-[180px]">OPTIONAL — UP TO 6 EXTRA PHOTOS. CUSTOMERS SEE THEM ON THE PRODUCT PAGE.</p>
          </div>
          {f.images.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {f.images.map((img, i) => (
                <div key={img + i} className="relative">
                  <img src={img} alt={`Angle ${i + 1}`} className="w-14 h-16 object-cover border border-white/15" />
                  <button
                    type="button"
                    data-testid={`product-extra-photo-remove-${i}`}
                    onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })}
                    aria-label={`Remove photo ${i + 1}`}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-volt text-void flex items-center justify-center"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <input className={inputCls} placeholder="FIT" value={f.fit} onChange={set("fit")} />
        <input className={`${inputCls} md:col-span-2`} placeholder="SIZES (comma separated: S, M, L, XL, XXL)" value={f.sizes} onChange={set("sizes")} />
        <textarea className={`${inputCls} md:col-span-3`} rows={3} placeholder={"COLOURS — one per line: Name|Hex|QikinkBaseSKU (the SKU of this colour's variant from YOUR Qikink catalog — size is auto-appended, e.g. base HYPERION-BLK + size M = HYPERION-BLK-M)"} value={f.colors} onChange={set("colors")} />
        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.25em] text-dim mb-2">PER-COLOUR PHOTOS — the photo customers see when they pick that colour</p>
          <div className="flex flex-wrap gap-2">
            {f.colors.split("\n").filter(Boolean).map((l) => {
              const name = l.split("|")[0]?.trim();
              if (!name) return null;
              const img = colorImages[name];
              return (
                <div key={name} className="flex items-center gap-2.5 border border-white/10 bg-[#0B0B0D] px-2.5 py-2">
                  {img
                    ? <img src={img} alt={name} data-testid={`colour-photo-thumb-${name.toLowerCase().replace(/\s+/g, "-")}`} className="w-9 h-11 object-cover border border-white/10" />
                    : <span className="w-9 h-11 border border-white/10 flex items-center justify-center font-mono text-[8px] text-mute">NONE</span>}
                  <div className="flex flex-col gap-1">
                    <p className="font-mono text-[9px] tracking-widest text-ink">{name.toUpperCase()}</p>
                    <div className="flex gap-2">
                      <button type="button" data-testid={`colour-photo-btn-${name.toLowerCase().replace(/\s+/g, "-")}`} disabled={colorPhotoBusy} onClick={() => { setColorTarget(name); colorFileRef.current?.click(); }} className="font-mono text-[9px] tracking-widest text-volt hover:text-acid transition-colors disabled:opacity-40">{colorPhotoBusy ? "UPLOADING…" : (img ? "CHANGE" : "UPLOAD")}</button>
                      {img && <button type="button" data-testid={`colour-photo-remove-${name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setColorImages((prev) => { const n = { ...prev }; delete n[name]; return n; })} className="font-mono text-[9px] text-mute hover:text-red-400 transition-colors">REMOVE</button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <input ref={colorFileRef} type="file" accept="image/*" className="hidden" data-testid="colour-photo-input" onChange={(e) => { uploadColourPhoto(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.25em] text-dim mb-2">QIKINK STORE-SKU MAP — one per line: Colour|Size|StoreSKU (the v-… code from your Qikink variant table; makes orders print WITH your design)</p>
          <textarea className={inputCls} rows={4} data-testid="variant-skus-input" placeholder={"Off White|M|v-9Rym1iCFaVZW18clNhcuubTeqgjX8XqU"} value={f.variant_skus} onChange={set("variant_skus")} />
        </div>
        <textarea className={`${inputCls} md:col-span-2`} rows={2} placeholder="DESCRIPTION" value={f.description} onChange={set("description")} />
        <label className="flex items-center gap-3 font-mono text-[10px] tracking-widest text-dim px-3">
          <input type="checkbox" checked={f.active} onChange={set("active")} className="accent-[#DFFF00]" /> ACTIVE ON STORE
        </label>
      </div>
      <button disabled={busy} type="submit" data-testid="admin-product-save-btn" className="bg-volt text-void font-display font-bold text-xs tracking-widest px-8 py-3 hover:bg-acid transition-colors disabled:opacity-40">
        {busy ? "SAVING…" : "SAVE PRODUCT"}
      </button>
    </form>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("orders");
  const [queries, setQueries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [returns, setReturns] = useState([]);
  const [restock, setRestock] = useState([]);
  const [qk, setQk] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testSku, setTestSku] = useState("");
  const [prefixes, setPrefixes] = useState("");

  const loadAll = async () => {
    try {
      const [o, p, c, q, qr, r, rs] = await Promise.all([
        api.get("/admin/orders"), api.get("/admin/products"),
        api.get("/admin/customers"), api.get("/admin/qikink/config"),
        api.get("/admin/queries"), api.get("/admin/returns"), api.get("/admin/restock"),
      ]);
      setOrders(o.data); setProducts(p.data); setCustomers(c.data); setQk(q.data);
      setQueries(qr.data); setReturns(r.data); setRestock(rs.data);
      setPrefixes((q.data.cod_restricted_prefixes || []).join(", "));
    } catch (e) {
      /* not authed */
    }
  };

  useEffect(() => {
    api.get("/admin/me").then(() => { setAuthed(true); loadAll(); }).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <main className="min-h-screen flex items-center justify-center font-mono text-[11px] tracking-widest text-mute">CHECKING SESSION…</main>;
  if (!authed) return <Login onLogin={() => { setAuthed(true); loadAll(); }} />;

  const updateStatus = async (num, status) => {
    try {
      await api.patch(`/admin/orders/${num}`, { status });
      toast.success(`${num} → ${status}`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
  };

  const syncOrder = async (num) => {
    setSyncingId(num);
    try {
      await api.post(`/admin/orders/${num}/sync`);
      toast.success(`${num} synced with Qikink`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const pushOrder = async (num) => {
    setSyncingId(num);
    try {
      await api.post(`/admin/orders/${num}/push`);
      toast.success(`${num} pushed to Qikink`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const deleteOrder = async (num) => {
    if (!window.confirm(`Delete test order ${num} permanently? This cannot be undone.`)) return;
    setSyncingId(num);
    try {
      await api.delete(`/admin/orders/${num}`);
      toast.success(`${num} deleted`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const reserveRestock = async (num) => {
    if (!window.confirm(`Reserve the restocked tee(s) for ${num} and skip Qikink printing? You then ship it from your place.`)) return;
    setSyncingId(num);
    try {
      await api.post(`/admin/orders/${num}/use-restock`);
      toast.success(`${num} — will ship from your restock`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const shipManual = async (num) => {
    const courier = window.prompt(`Courier name for ${num} (e.g. Delhivery, Bluedart, India Post):`);
    if (!courier) return;
    const awb = window.prompt("Tracking / AWB number:");
    if (!awb) return;
    setSyncingId(num);
    try {
      await api.post(`/admin/orders/${num}/ship-manual`, { courier, awb });
      toast.success(`${num} marked shipped — customer sees tracking ${awb}`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const shipShiprocket = async (num) => {
    if (!window.confirm(`Create a Shiprocket pickup + label for ${num}? Shiprocket assigns the courier and AWB.`)) return;
    setSyncingId(num);
    try {
      const { data } = await api.post(`/admin/orders/${num}/ship-shiprocket`);
      toast.success(`${num} shipped via Shiprocket — AWB ${data.shipping?.awb || "—"}`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const syncShiprocket = async (num) => {
    setSyncingId(num);
    try {
      await api.post(`/admin/orders/${num}/shiprocket/sync`);
      toast.success(`${num} tracking synced`);
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
    setSyncingId(null);
  };

  const removeProduct = async (pid) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${pid}`); toast.success("Product deleted"); loadAll(); }
    catch (e) { toast.error(apiErrorDetail(e)); }
  };

  const testPush = async () => {
    try {
      const { data } = await api.post("/admin/qikink/test-push", { sku: testSku.trim() || "USs-Wh-M" });
      setTestResult(JSON.stringify(data, null, 2));
      toast.success(`Test push OK (${data.mode})`);
    } catch (e) { setTestResult(JSON.stringify({ error: apiErrorDetail(e) }, null, 2)); toast.error("Test push failed"); }
  };

  const testColourSku = async (code, colour, baseSku) => {
    try {
      const { data } = await api.post("/admin/qikink/test-push", { sku: `${baseSku}-M` });
      setTestResult(JSON.stringify(data, null, 2));
      toast.success(`${code} / ${colour}: SKU ACCEPTED — Qikink order ${data?.response?.order_id || "created"}`);
    } catch (e) {
      setTestResult(JSON.stringify({ sku: `${baseSku}-M`, error: apiErrorDetail(e) }, null, 2));
      toast.error(`${code} / ${colour}: ${apiErrorDetail(e)}`);
    }
  };

  const savePrefixes = async () => {
    try {
      await api.patch("/admin/qikink/cod-prefixes", { prefixes: prefixes.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("COD restrictions saved");
      loadAll();
    } catch (e) { toast.error(apiErrorDetail(e)); }
  };

  const logout = async () => { await api.post("/admin/logout"); setAuthed(false); };

  return (
    <main className="min-h-screen px-5 md:px-10 pt-24 pb-24 max-w-[1600px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 pt-8 pb-6 border-b border-white/10">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-volt">RVIOR COMMAND CENTER</p>
          <h1 className="font-display font-extrabold uppercase tracking-tighter text-2xl mt-1">OPERATIONS</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-widest px-3 py-1.5 border border-white/15 text-dim">
            QIKINK: <span className={qk?.mode === "mock" ? "text-volt" : "text-acid"}>{(qk?.mode || "…").toUpperCase()}</span>
          </span>
          <button onClick={logout} className="font-mono text-[10px] tracking-widest border border-white/15 px-4 py-2 hover:border-volt hover:text-volt transition-colors">LOGOUT</button>
        </div>
      </div>

      <div className="flex gap-2 mt-6 flex-wrap">
        {[["orders", "ORDERS", "admin-orders-tab"], ["returns", "RETURNS", "admin-returns-tab"], ["products", "PRODUCTS", "admin-products-tab"], ["customers", "CUSTOMERS", "admin-customers-tab"], ["queries", "QUERIES", "admin-queries-tab"], ["qikink", "QIKINK", "admin-qikink-tab"]].map(([k, label, tid]) => (
          <button key={k} data-testid={tid} onClick={() => setTab(k)}
            className={`font-mono text-[11px] tracking-[0.2em] px-6 py-3 border transition-colors ${tab === k ? "bg-ink text-void border-ink font-bold" : "border-white/15 text-dim hover:border-acid hover:text-acid"}`}>
            {label}{k === "returns" && returns.some((r) => r.status === "requested") ? <span className="ml-2 text-volt">●</span> : null}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="mt-8 overflow-x-auto border border-white/10">
          <table className="w-full font-mono text-[11px]">
            <thead>
              <tr className="bg-[#0E1015] text-mute tracking-widest text-left">
                {["ORDER", "DATE", "CUSTOMER", "UNITS", "TOTAL", "PAYMENT", "STATUS", "QIKINK", "ACTIONS"].map((h) => (
                  <th key={h} className="px-4 py-3 border-b border-white/10 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_number} className="border-b border-white/[0.06] hover:bg-white/[0.02] align-top">
                  <td className="px-4 py-3 text-acid">{o.order_number}</td>
                  <td className="px-4 py-3 text-dim">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-4 py-3">{o.customer.first_name}<br /><span className="text-mute">{o.customer.phone}</span></td>
                  <td className="px-4 py-3 text-dim">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                  <td className="px-4 py-3">₹{o.pricing.total.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <span className={o.payment.method === "cod" ? "text-[#22C55E]" : "text-[#3B82F6]"}>
                      {o.payment.method.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select data-testid={`order-status-select-${o.order_number}`} value={o.status} onChange={(e) => updateStatus(o.order_number, e.target.value)}
                      className="bg-[#151821] border border-white/15 px-2 py-1.5 text-[10px] focus:outline-none">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span className={`block mt-1 text-[9px] ${STATUS_COLOR[o.status]}`}>{o.status}</span>
                    {o.return?.status && (
                      <span data-testid={`order-return-badge-${o.order_number}`} className="block mt-1 text-[9px] text-[#F59E0B]">RETURN: {o.return.status.toUpperCase()}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-dim">
                    {o.qikink?.submitted ? (
                      <>
                        <span className="text-[#10B981]">{(o.qikink.status || "—").toUpperCase()}</span>
                        <br /><span className="text-mute text-[9px]">{o.qikink.order_id || ""}{o.qikink.awb ? ` · AWB ${o.qikink.awb}` : ""}</span>
                      </>
                    ) : (o.fulfilment?.mode === "restock" || o.fulfilment?.hint === "restock_available") ? (
                      <>
                        <span className={o.fulfilment?.mode === "restock" ? "text-[#3B82F6]" : "text-[#F59E0B]"}>
                          {o.fulfilment?.mode === "restock" ? "RESTOCK MODE" : "RESTOCK MATCH"}
                        </span>
                        {o.shipping?.awb && <><br /><span className="text-mute text-[9px]">{o.shipping.courier || ""} · AWB {o.shipping.awb}</span></>}
                      </>
                    ) : (
                      <>
                        <span className="text-volt">NOT SUBMITTED</span>
                        {o.qikink?.error && <br />}
                        {o.qikink?.error && <span className="text-mute text-[9px]">{o.qikink.error.slice(0, 60)}</span>}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {o.qikink?.submitted ? (
                      <button data-testid={`order-sync-btn-${o.order_number}`} onClick={() => syncOrder(o.order_number)} disabled={syncingId === o.order_number}
                        className="flex items-center gap-1.5 border border-white/20 px-3 py-1.5 hover:border-acid hover:text-acid transition-colors disabled:opacity-40">
                        <RefreshCw size={10} className={syncingId === o.order_number ? "animate-spin" : ""} /> SYNC
                      </button>
                    ) : o.fulfilment?.hint === "restock_available" && !o.fulfilment?.mode ? (
                      <>
                        <button data-testid={`order-use-restock-btn-${o.order_number}`} onClick={() => reserveRestock(o.order_number)} disabled={syncingId === o.order_number}
                          className="flex items-center gap-1.5 bg-[#3B82F6] text-void px-3 py-1.5 font-bold hover:opacity-80 transition-opacity disabled:opacity-40">
                          <RotateCcw size={10} /> USE RESTOCK
                        </button>
                        <button data-testid={`order-push-btn-${o.order_number}`} onClick={() => pushOrder(o.order_number)} disabled={syncingId === o.order_number}
                          title="Print a fresh unit via Qikink instead"
                          className="mt-1.5 flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-mute hover:border-acid hover:text-acid transition-colors disabled:opacity-40">
                          <Send size={10} /> PUSH TO QIKINK
                        </button>
                      </>
                    ) : o.fulfilment?.mode === "restock" && !o.shipping?.awb ? (
                      <>
                        {qk?.shiprocket_connected && (
                          <button data-testid={`order-shiprocket-btn-${o.order_number}`} onClick={() => shipShiprocket(o.order_number)} disabled={syncingId === o.order_number}
                            className="flex items-center gap-1.5 bg-volt text-void px-3 py-1.5 font-bold hover:bg-acid transition-colors disabled:opacity-40">
                            <Truck size={10} /> SHIPROCKET
                          </button>
                        )}
                        <button data-testid={`order-ship-manual-btn-${o.order_number}`} onClick={() => shipManual(o.order_number)} disabled={syncingId === o.order_number}
                          className="mt-1.5 flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-mute hover:border-acid hover:text-acid transition-colors disabled:opacity-40">
                          <Truck size={10} /> ENTER TRACKING
                        </button>
                      </>
                    ) : o.shipping?.method === "shiprocket" ? (
                      <button data-testid={`order-sr-sync-btn-${o.order_number}`} onClick={() => syncShiprocket(o.order_number)} disabled={syncingId === o.order_number}
                        className="flex items-center gap-1.5 border border-white/20 px-3 py-1.5 hover:border-acid hover:text-acid transition-colors disabled:opacity-40">
                        <RefreshCw size={10} className={syncingId === o.order_number ? "animate-spin" : ""} /> SYNC SR
                      </button>
                    ) : (
                      <button data-testid={`order-push-btn-${o.order_number}`} onClick={() => pushOrder(o.order_number)} disabled={syncingId === o.order_number}
                        className="flex items-center gap-1.5 bg-volt text-void px-3 py-1.5 font-bold hover:bg-acid transition-colors disabled:opacity-40">
                        <Send size={10} /> PUSH
                      </button>
                    )}
                    {o.shipping?.label_url && (
                      <a href={o.shipping.label_url} target="_blank" rel="noreferrer" className="block mt-1.5 font-mono text-[9px] text-acid underline underline-offset-2">LABEL</a>
                    )}
                    <button data-testid={`order-delete-btn-${o.order_number}`} onClick={() => deleteOrder(o.order_number)} disabled={syncingId === o.order_number}
                      title="Delete test order"
                      className="mt-1.5 flex items-center gap-1.5 border border-white/20 px-2.5 py-1.5 text-mute hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-40">
                      <Trash2 size={10} />
                    </button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-mute">NO ORDERS YET — SHARE THE DROP.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "returns" && (
        <AdminReturns returns={returns} restock={restock} products={products} shiprocketOn={qk?.shiprocket_connected} onReload={loadAll} />
      )}

      {tab === "products" && (
        <div className="mt-8">
          {!showNew && !editing && (
            <button data-testid="admin-add-product-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-volt text-void font-display font-bold text-xs tracking-widest px-6 py-3 hover:bg-acid transition-colors mb-6">
              <Plus size={13} /> ADD PRODUCT
            </button>
          )}
          {showNew && <ProductForm onSaved={() => { setShowNew(false); loadAll(); }} onCancel={() => setShowNew(false)} />}
          {editing && <ProductForm initial={editing} onSaved={() => { setEditing(null); loadAll(); }} onCancel={() => setEditing(null)} />}
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="bg-[#0E1015] text-mute tracking-widest text-left">
                  {["PHOTO", "CODE", "PRODUCT", "PRICE", "COLOURS / QIKINK SKUS", "SIZES", "STATE", "ACTIONS"].map((h) => (
                    <th key={h} className="px-4 py-3 border-b border-white/10 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p._id} className="border-b border-white/[0.06] hover:bg-white/[0.02]">
                    <td className="px-4 py-3"><img src={p.image} alt={p.title} className="w-10 h-12 object-cover" /></td>
                    <td className="px-4 py-3 text-acid">{p.code}</td>
                    <td className="px-4 py-3">{p.title}</td>
                    <td className="px-4 py-3">₹{p.price.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {p.colors.map((c) => (
                          <span key={c.name} className="inline-flex items-center gap-2 border border-white/15 px-2 py-1 text-dim">
                            {c.name}({c.sku || "NO SKU"})
                            {c.sku && (
                              <button
                                data-testid={`qikink-test-sku-${p.code}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => testColourSku(p.code, c.name, c.sku)}
                                className="text-volt hover:text-acid underline underline-offset-2 transition-colors"
                                title="Sends a real test order to Qikink sandbox with this colour's SKU + size M"
                              >
                                TEST
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-dim">{p.sizes.join(", ")}</td>
                    <td className={`px-4 py-3 ${p.active ? "text-[#22C55E]" : "text-volt"}`}>{p.active ? "LIVE" : "HIDDEN"}</td>
                    <td className="px-4 py-3 flex gap-2">
                      <button data-testid={`product-edit-btn-${p.code}`} onClick={() => { setEditing(p); setShowNew(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="border border-white/20 px-3 py-1.5 hover:border-acid hover:text-acid transition-colors">EDIT</button>
                      <button data-testid={`product-delete-btn-${p.code}`} onClick={() => removeProduct(p._id)} className="border border-white/20 px-3 py-1.5 hover:border-volt hover:text-volt transition-colors">DELETE</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "queries" && (
        <div className="border border-white/10 bg-[#0E1015]">
          {queries.length === 0 ? (
            <p className="p-8 font-mono text-[11px] tracking-widest text-mute">NO QUERIES YET — CUSTOMER MESSAGES FROM THE CONTACT PAGE LAND HERE.</p>
          ) : queries.map((qr) => (
            <div key={qr._id} data-testid={`admin-query-${qr._id}`} className="p-5 border-b border-white/10 last:border-b-0">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <p className="font-display text-sm font-bold tracking-widest">{qr.name}</p>
                <p className="font-mono text-[10px] text-mute">{new Date(qr.created_at).toLocaleString("en-IN")}</p>
              </div>
              <a href={`mailto:${qr.email}`} data-testid={`admin-query-email-${qr._id}`} className="font-mono text-[10px] text-volt underline underline-offset-2">{qr.email}</a>
              <p className="font-mono text-[11px] text-dim mt-2 whitespace-pre-wrap leading-relaxed">{qr.message}</p>
            </div>
          ))}
        </div>
      )}
      {tab === "customers" && (
        <div className="mt-8 overflow-x-auto border border-white/10">
          <table className="w-full font-mono text-[11px]">
            <thead>
              <tr className="bg-[#0E1015] text-mute tracking-widest text-left">
                {["NAME", "EMAIL", "PHONE", "CITY", "ORDERS", "SPEND", "LAST ORDER"].map((h) => (
                  <th key={h} className="px-4 py-3 border-b border-white/10 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c._id} className="border-b border-white/[0.06] hover:bg-white/[0.02]">
                  <td className="px-4 py-3">{c.name} {c.last_name}</td>
                  <td className="px-4 py-3 text-acid">{c._id}</td>
                  <td className="px-4 py-3 text-dim">{c.phone}</td>
                  <td className="px-4 py-3 text-dim">{c.city}</td>
                  <td className="px-4 py-3">{c.orders}</td>
                  <td className="px-4 py-3 text-acid">₹{c.spend.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-dim">{new Date(c.last_order).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-mute">NO CUSTOMERS YET.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "qikink" && (
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-[#0E1015] p-6">
            <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-5">QIKINK OPEN API — STATUS</p>
            <div className="space-y-2 font-mono text-[11px] text-dim">
              <p>MODE: <span className={qk?.mode === "mock" ? "text-volt" : "text-acid"}>{(qk?.mode || "").toUpperCase()}</span></p>
              <p>BASE URL: <span className="text-ink">{qk?.base_url || "—"}</span></p>
              <p>ORDER ENDPOINT: <span className="text-ink">{qk?.order_endpoint || "—"}</span></p>
              <p>STATUS ENDPOINT: <span className="text-ink">{qk?.status_endpoint || "—"}</span></p>
              <p>PAYMENTS: <span className={qk?.prepaid_enabled ? "text-acid" : "text-volt"}>{qk?.prepaid_enabled ? `RAZORPAY (${(qk?.payment_mode || "test").toUpperCase()} MODE)` : "PREPAID OFFLINE (COD ONLY)"}</span></p>
              <p className="text-mute pt-2 leading-relaxed">
                To create real orders, every colour's base SKU below must match a variant SKU in YOUR Qikink catalog (Qikink dashboard → Products). The store appends the size automatically: base RVT-AB + size M → RVT-AB-M. Map them in the PRODUCTS tab per colour, or update them directly in Qikink to match.
              </p>
            </div>
            <div className="mt-6">
              <label className="block font-mono text-[10px] tracking-[0.25em] text-dim mb-2">SKU FROM YOUR QIKINK CATALOG (ANY VARIANT, E.G. USs-Wh-M)</label>
              <div className="flex gap-2">
                <input data-testid="qikink-test-sku-input" className={inputCls} placeholder="USs-Wh-M" value={testSku} onChange={(e) => setTestSku(e.target.value)} />
                <button data-testid="admin-qikink-test-push-btn" onClick={testPush} className="shrink-0 bg-volt text-void font-display font-bold text-xs tracking-widest px-6 py-2.5 hover:bg-acid transition-colors flex items-center gap-2">
                  <Send size={12} /> TEST PUSH
                </button>
              </div>
              <p className="mt-2 font-mono text-[9px] tracking-widest text-mute">SENDS A REAL ORDER TO QIKINK SANDBOX WITH THIS SKU</p>
            </div>
            {testResult && (
              <pre data-testid="qikink-test-push-result" className="mt-4 border border-white/10 bg-[#060709] p-4 text-[10px] text-dim overflow-x-auto max-h-72">{testResult}</pre>
            )}
          </div>
          <div className="border border-white/10 bg-[#0E1015] p-6">
            <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-5">COD RESTRICTIONS</p>
            <p className="font-mono text-[10px] text-mute leading-relaxed mb-4">
              Comma-separated pincode prefixes where COD is disabled (e.g. "19" blocks 190xxx). All other pincodes accept COD.
            </p>
            <input data-testid="cod-prefixes-input" className={inputCls} placeholder="19, 78" value={prefixes} onChange={(e) => setPrefixes(e.target.value)} />
            <button data-testid="cod-prefixes-save-btn" onClick={savePrefixes} className="mt-4 border border-white/20 font-mono text-[10px] tracking-widest px-6 py-2.5 hover:border-acid hover:text-acid transition-colors">
              SAVE RESTRICTIONS
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
